import type { IFormDefinition, IRecordTypeDefinition, IRecordTypeOverride } from '@platform/core/types';
import { editorWritten, editorTabs, fullMark, isForcedFull, isKb, keyToggles, tagsKeyOf, visibleFields } from './formRules';
import { explicitWidthsOf } from './formWidths';
import { modelGroupsOf } from './kbGroups';
import type { IProtoForm } from './IProtoForm';
import { standardFormOf } from './standardForm';

/** Форма — макет простору, засіяний прототипом (layout реєстрів, фінансів, довідників): без вкладки «Доручення» (tabs.assignments: false). */
export const isSeededLayout = (rt: IRecordTypeDefinition): boolean => !rt.form.tabs.includes('assignments');

/**
 * Модель редактора з форми визначення: ключовий рядок → поля ключового рядка, поля під назвою → теги й опис, вкладки, групи (ключі — за
 * розкладкою групи, де стоїть і виконавець, якого запис у групах не показує; «Увесь рядок» групи з примусовим повним рядком — відмітка
 * fullMark у збереженій редактором формі), приховані — не розміщені ніде. У засіяному макеті без виконавця в групах він стає першим у
 * першій групі (як layout реєстрів прототипу). Чиста функція.
 */
export function readForm(rt: IRecordTypeDefinition, form: IFormDefinition, editor: boolean): IProtoForm {
  const fields = visibleFields(rt);
  const has = (k: string) => fields.some((f) => f.key === k);
  const kb = isKb(rt);
  const tags = tagsKeyOf(fields);
  const key = Object.fromEntries(keyToggles.map((k) => [k, !has(k) || (kb && k !== 'status') || form.keyLine.includes(k)])) as IProtoForm['key'];
  const showTags = !tags || form.underTitle.includes(tags);
  const showDesc = kb || !has('description') || form.underTitle.includes('description');
  const groups = form.groups.map((g) => {
    const laid = (g.layout ?? []).map((l) => l.key).filter((k) => k !== fullMark);
    const keys = [...laid, ...g.fields.filter((k) => !laid.includes(k))].filter((k, i, all) => has(k) && all.indexOf(k) === i);
    const full = editor && isForcedFull(g.title) ? (g.layout ?? []).some((l) => l.key === fullMark) : !!g.full;
    return { id: g.id, title: g.title, full, keys };
  });
  const placed = () => new Set(groups.flatMap((g) => g.keys));
  if (!editor && has('assignee') && !placed().has('assignee') && groups[0]) groups[0] = { ...groups[0], keys: ['assignee', ...groups[0].keys] };
  const shown = placed();
  const covered = (k: string) => k === 'title' || (key.status && k === 'status') || (showTags && k === tags) || (showDesc && !kb && k === 'description');
  return {
    key, showTags, showDesc,
    tabs: Object.fromEntries(editorTabs.map((t) => [t, form.tabs.includes(t)])) as IProtoForm['tabs'],
    groups,
    hidden: fields.map((f) => f.key).filter((k) => !shown.has(k) && !covered(k)),
    widths: {},
  };
}

/**
 * Модель редактора форми типу записів у просторі (прототип v4 cust-form): форма, збережена редактором (патч `forms.full` з ключовим рядком,
 * полями під назвою чи вкладками), — як є; засіяний прототипом макет — з форми визначення; інакше — стандартний макет. Явні ширини — з
 * перевизначень полів. Чиста функція.
 */
export function protoFormOf(rt: IRecordTypeDefinition, patch: IRecordTypeOverride): IProtoForm {
  const widths = explicitWidthsOf(patch);
  const full = patch.forms?.full;
  if (editorWritten(patch) && full) {
    const form: IFormDefinition = { keyLine: full.keyLine ?? rt.form.keyLine, underTitle: full.underTitle ?? rt.form.underTitle, groups: full.groups ?? rt.form.groups, tabs: full.tabs ?? rt.form.tabs };
    const read = readForm(rt, form, true);
    return { ...read, groups: modelGroupsOf(rt, read), widths };
  }
  if (isSeededLayout(rt)) return { ...readForm(rt, rt.form, false), widths };
  return { ...standardFormOf(rt, rt.form.tabs), widths };
}
